import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Link,
  Stack,
  Typography,
} from '@mui/material';

import { PatientService } from '../../../api/patient.service';
import { RefractionService } from '../../../api/refraction.service';
import { NhsEligibility, SubmitClaimPayload } from '../../../@types/pcse';
import VoucherClaimForm from '../../../components/claims/VoucherClaimForm';
import {
  readPrescription,
  ReadPrescriptionResult,
} from '../../../components/claims/readPrescription';

interface PatientNhsClaimsProps {
  patientId?: string;
}

/**
 * "NHS Claims" tab — GOS3 optical voucher claim for the current patient.
 *
 * Loads the full patient via /patients/:id (the tab's own fetch: the parent page
 * uses /patients/:id/info, which does NOT include the patientNhs relation), then
 * hands the claim form the patient's PatientNhs record — the authoritative
 * eligibility source. Eligibility itself is edited on Add/Edit Patient, not here.
 */
export default function PatientNhsClaims({ patientId }: PatientNhsClaimsProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [patientNhsId, setPatientNhsId] = useState<number | undefined>();
  const [eligibility, setEligibility] = useState<
    Partial<NhsEligibility> | undefined
  >();
  const [patient, setPatient] = useState<
    Partial<SubmitClaimPayload['patient']> | undefined
  >();
  const [prescription, setPrescription] = useState<
    ReadPrescriptionResult | undefined
  >();
  const [testDate, setTestDate] = useState<string | undefined>();

  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await new PatientService().getPatientByApi(patientId);
      const p = res.data as any;

      setPatient({
        firstName: p?.firstName ?? '',
        lastName: p?.surName ?? p?.lastName ?? '',
        dateOfBirth: p?.date_of_birth
          ? String(p.date_of_birth).slice(0, 10)
          : '',
        postcode: p?.postCode ?? '',
      });
      setPatientNhsId(p?.patientNhs?.id);
      setEligibility(p?.patientNhs?.nhsEligibility);

      // The refraction drives the voucher band, so load it here rather than
      // making the dispenser read it off another tab and pick a band by hand.
      // A missing refraction is not an error — the form falls back to a manual
      // choice and says so.
      try {
        const latest = await new RefractionService().getLatestRefraction(
          patientId,
        );
        setPrescription(readPrescription(latest?.final));
        setTestDate(
          latest?.createdDate
            ? String(latest.createdDate).slice(0, 10)
            : undefined,
        );
      } catch {
        setPrescription(undefined);
      }
    } catch (err: any) {
      setError(
        typeof err === 'string'
          ? err
          : (err?.message ?? 'Could not load the patient record.'),
      );
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!patientId) {
    return (
      <Alert severity="info">Select a patient to raise an NHS claim.</Alert>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={5}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Typography variant="h5">NHS optical voucher claim (GOS3)</Typography>

      {patientNhsId ? (
        <Alert severity="success">
          Using this patient&apos;s saved NHS eligibility record (#
          {patientNhsId}). It is the authoritative source — if it differs from
          the tick-boxes below, the saved record is what gets sent to PCSE.
        </Alert>
      ) : (
        <Alert severity="warning">
          This patient has no saved NHS eligibility record. The claim will use
          the eligibility you tick below. To store it against the patient, set
          the <strong>NHS Eye Test Eligibility</strong> section on{' '}
          <Link href="#" underline="always">
            Edit Patient
          </Link>
          .
        </Alert>
      )}

      <VoucherClaimForm
        patientId={patientId}
        patientNhsId={patientNhsId}
        initialEligibility={eligibility}
        initialPatient={patient}
        initialPrescription={prescription}
        initialTestDate={testDate}
        onSubmitted={() => load()}
      />
    </Stack>
  );
}
